// Ticket-Status (Anzeige): Spiegel von api/src/lib/ticketStatus.ts — beide Listen müssen übereinstimmen.
// offen → in_arbeit ⇄ rueckfrage → erledigt | abgelehnt

export const TICKET_STATUS = ['offen', 'in_arbeit', 'rueckfrage', 'erledigt', 'abgelehnt'] as const;
export type TicketStatus = typeof TICKET_STATUS[number];

export const STATUS_INFO: Record<TicketStatus, { label: string; cls: string; melder: string }> = {
  offen:      { label: 'Offen',      cls: 'bg-rose-100 text-rose-700',       melder: 'Gemeldet — wartet auf Bearbeitung' },
  in_arbeit:  { label: 'In Arbeit',  cls: 'bg-sky-100 text-sky-700',         melder: 'Wird bearbeitet' },
  rueckfrage: { label: 'Rückfrage',  cls: 'bg-amber-100 text-amber-800',     melder: 'Wartet auf deine Antwort' },
  erledigt:   { label: 'Erledigt',   cls: 'bg-emerald-100 text-emerald-700', melder: 'Erledigt' },
  abgelehnt:  { label: 'Abgelehnt',  cls: 'bg-slate-200 text-slate-700',     melder: 'Abgelehnt' },
};

export const statusInfo = (s: string) => STATUS_INFO[s as TicketStatus] ?? STATUS_INFO.offen;
export const istAbgeschlossen = (s: string): boolean => s === 'erledigt' || s === 'abgelehnt';

export type Schritt = { key: string; label: string; zustand: 'fertig' | 'aktiv' | 'offen' | 'warnung' | 'abgelehnt' };

/** Fortschrittsleiste für den Melder: Gemeldet → In Arbeit → Erledigt (bzw. Abgelehnt) */
export function stepper(status: string): Schritt[] {
  const s = status as TicketStatus;
  const letzter = s === 'abgelehnt' ? { key: 'ende', label: 'Abgelehnt', zustand: 'abgelehnt' as const }
    : { key: 'ende', label: 'Erledigt', zustand: s === 'erledigt' ? 'fertig' as const : 'offen' as const };
  const mitte: Schritt['zustand'] = s === 'offen' ? 'offen'
    : s === 'rueckfrage' ? 'warnung'
    : s === 'in_arbeit' ? 'aktiv'
    : s === 'abgelehnt' ? 'fertig' : 'fertig';
  return [
    { key: 'gemeldet', label: 'Gemeldet', zustand: s === 'offen' ? 'aktiv' : 'fertig' },
    { key: 'arbeit', label: s === 'rueckfrage' ? 'Rückfrage' : 'In Arbeit', zustand: mitte },
    letzter,
  ];
}

export function fmtDatum(v?: string | null): string {
  if (!v) return '—';
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : v;
}
export function fmtDatumZeit(v?: string | null): string {
  if (!v) return '—';
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}` : fmtDatum(v);
}

/** „vor 5 Min“, „vor 3 Std“, „vor 2 Tagen“ — ältere als Datum. D1-Zeiten sind UTC ohne „Z“. */
export function relativZeit(v?: string | null, jetzt: number = Date.now()): string {
  if (!v) return '—';
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(v) ? v : v.replace(' ', 'T') + 'Z';
  const t = Date.parse(iso);
  if (isNaN(t)) return fmtDatum(v);
  const min = Math.round((jetzt - t) / 60000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} Min`;
  const std = Math.round(min / 60);
  if (std < 24) return `vor ${std} Std`;
  const tage = Math.round(std / 24);
  if (tage < 7) return tage === 1 ? 'gestern' : `vor ${tage} Tagen`;
  return fmtDatum(v);
}
